import {defineStore} from "pinia";
import {DefaultPaginationItems, defaultTake, type PaginationItems} from "@/models/pagination";
import {
    DefaultUserModel,
    type UpdateUserStatusRequest,
    UpdateUserStatusRequestDefaults,
    type User,
    type UserListRequest
} from "@/models/user/user";
import {createUser, getUsers, updateUser, updateUserStatus, getUserTableDensity, updateUserTableDensity, getUserRelations} from '@/services/user/user-service';
import {DefaultDrawer, type Drawer} from "@/models/drawer";
import {cloneDeep} from "lodash";
import { mapTableDensity, type DensitySize, TableDensity } from '@/constants/density-size';
import { DefaultFilterRequest } from "@/models/filter";
import {RelationDropdownModel} from "@/models/relation/relation";

interface userState {
    user: User;
    userDetailDrawer: Drawer;
    users: PaginationItems<User>;
    usersListRequest: UserListRequest;
    loading: boolean;
    userStatusDrawer: Drawer;
    updateUserStatusRequest: UpdateUserStatusRequest;
    userRelations: RelationDropdownModel[];
}

export const useUserStore = defineStore({
    id: 'users',
    state: () => ({
        user: DefaultUserModel(),
        userDetailDrawer: DefaultDrawer(),
        users: {
            items: [],
            pageCount: 0,
            totalItems: 0,
        },
        usersListRequest: {
            filterOptions: DefaultFilterRequest(),
            page: 1,
            take: defaultTake,
        },
        loading: false,
        userStatusDrawer: DefaultDrawer(),
        updateUserStatusRequest: UpdateUserStatusRequestDefaults(),
        chauffeurs: [],
        userRelations: []
    } as userState),
    actions: {
        async fetchUsers() {
            this.loading = true;
            this.users = DefaultPaginationItems();

            const response = await getUsers(this.usersListRequest)
            if (response.status === 200) {
                this.users = response.data;
            }

            this.loading = false;
        },
        async updateUserStatus() {
            this.loading = true;
            const response = await updateUserStatus(this.updateUserStatusRequest)
            this.loading = false;
            return response.status === 200;
        },
        async openCreateDrawer() {
            this.user = DefaultUserModel();
            this.userDetailDrawer.visible = true;
            this.userDetailDrawer.loading = false;
        },
        async openUpdateDrawer(user: User) {
            this.user = cloneDeep(user);
            this.userDetailDrawer.visible = true;
            this.userDetailDrawer.loading = false;
        },
        async openStatusDrawer(user: User) {
            this.updateUserStatusRequest.active = !user.status.active;
            this.updateUserStatusRequest.userId = user.id !== undefined ? user.id : '';
            this.userStatusDrawer.visible = true;
            this.userStatusDrawer.loading = false;
        },
        async createOrUpdateUser() {
            this.userDetailDrawer.loading = true;
            let response = undefined;
            if (this.user?.id) {
                response = await updateUser(this.user);
            } else {
                response = await createUser(this.user);
            }

            if (response.status == 200) {
                this.user = response.data;
                this.userDetailDrawer.visible = false;
            }

            this.userDetailDrawer.loading = false;

            return response;
        },
        async getUserTableDensity(userId: string): Promise<DensitySize> {
            const response = await getUserTableDensity(userId);
            if (response.status === 200) {
                return mapTableDensity(response.data as TableDensity);
            }
            else {
                return 'standard';
            }
        },
        async updateUserTableDensity(userId: string, density: DensitySize) {
            const response = await updateUserTableDensity(
                {
                    id: userId, 
                    tableDensity: mapTableDensity(density)
                }
            );
            return response.status === 200;
        },
        async fetchUserRelations(userId: string) {
            const response = await getUserRelations(userId);
            
            if (response.status === 200) {
                this.userRelations = response.data as RelationDropdownModel[];
            }
            else {
                this.userRelations = [];
            }
        },
    }
})